import { Adb } from '@/api/adb/adb';
import { Configuration, Files } from '@/entities/configuration';
import { logger } from '@/api/logger';

export function filterFiles(
    files: string[],
    exists: string[],
    override: boolean,
    overrideLargesFiles: boolean
): string[] {
    if (override && overrideLargesFiles) return files;
    if (override)
        return files.filter((file) => !isAdditionalFiles(file) || exists.indexOf(file) == -1);
    return files.filter((file) => exists.indexOf(file) == -1);
}

/**
 * Une entree `files` peut appartenir a une ou plusieurs applications (`requires`) : les
 * localisations et les voix d'Endocare, par exemple, sont declarees au niveau de la
 * configuration alors qu'elles ne servent qu'aux apps Endocare. Sans licence pour elles,
 * inutile de recopier des centaines de fichiers sur le casque.
 *
 * Sans `requires` (ou avec un tableau vide), les fichiers sont communs a toutes les
 * configurations et toujours pousses.
 */
export function isFilesAuthorized(remote: Files, isAuthorized: (pkg: string) => boolean) {
    if (!remote.requires?.length) return true;
    return remote.requires.some(isAuthorized);
}

export const PARAMETERS_FILE = 'parameters.pem';

/**
 * La home Bliss et les mondes (kaitei, mori et ses variantes, nohara, uchuu), quel que
 * soit l'editeur du paquet (`com.neodtx.bliss.*`, `com.butterflytx.bliss.*`,
 * `com.Enozone.bliss.vr.*`). Ce sont les apps a reinstaller a neuf quand on change de
 * configuration ; le reste (O2, Wild Immersion, Endocare, maintenance) peut rester.
 */
export function isHomeOrWorld(pkg: string): boolean {
    const parts = pkg.split('.');
    const i = parts.indexOf('bliss');
    if (i == -1) return false;
    const rest = parts.slice(i + 1);
    if (rest[0] == 'vr') rest.shift();
    return ['home', 'one', 'kaitei', 'mori', 'nohara', 'uchuu'].includes(rest[0]);
}

/**
 * Entree `files` qui fournit le `parameters.pem` du casque, et donc la version de
 * reference cote serveur. Les fichiers propres au casque l'emportent sur ceux de la
 * configuration (apps puis fichiers communs), comme a la copie ou ils sont joues en
 * dernier et ecrasent l'equivalent generique.
 */
export function findParametersFiles(
    deviceFiles: Files[],
    configuration: Configuration | null
): Files | null {
    const candidates: Files[] = [...deviceFiles];
    if (configuration) {
        for (const pkg of configuration.applications.packages)
            candidates.push(...(pkg.files ?? []));
        candidates.push(...(configuration.files ?? []));
    }
    return candidates.find((remote) => remote.files.includes(PARAMETERS_FILE)) ?? null;
}

function isAdditionalFiles(file: string): boolean {
    const ext = file.split('.').pop()!.trim();
    return ['zip', 'mp4', 'tbe', 'wav'].indexOf(ext) > -1;
}

/**
 * Taille annoncee par le serveur, sans telecharger le fichier.
 * `null` si l'information n'est pas disponible (404, reseau, en-tete absent).
 *
 * On demande un seul octet plutot que d'utiliser HEAD : la configuration CORS de
 * files.neodtx.com n'autorise que `GET, POST, OPTIONS`, donc une requete HEAD est
 * rejetee par le navigateur. La reponse 206 porte `Content-Range: bytes 0-0/<taille>`,
 * et `Content-Range` fait partie des en-tetes exposes par le serveur.
 */
async function remoteSize(url: string): Promise<number | null> {
    try {
        const res = await fetch(url, { headers: { Range: 'bytes=0-0' } });
        if (!res.ok) return null;
        const range = res.headers.get('Content-Range');
        // 206 : la taille totale est apres le `/`. 200 : le serveur a ignore le
        // Range, `Content-Length` porte alors la taille complete.
        const total = range != null ? range.split('/').pop() : res.headers.get('Content-Length');
        if (total == null) return null;
        const size = Number.parseInt(total);
        return Number.isNaN(size) ? null : size;
    } catch {
        return null;
    }
}

/**
 * Parmi les fichiers presents sur le casque par leur nom, ne garde que ceux dont
 * la taille correspond a celle annoncee par le serveur.
 *
 * Un fichier de taille differente (transfert interrompu, contenu mis a jour) est
 * retire de la liste : il sera donc traite comme absent et recopie.
 *
 * Si la taille attendue est inconnue, le fichier est considere a jour : on ne
 * declenche pas un retransfert de plusieurs centaines de Mo sur un alea reseau.
 */
export async function filterUpToDate(adb: Adb, remote: Files, exists: string[]) {
    if (exists.length == 0) return exists;
    const sizes = await adb.fileSizes(exists.map((file) => `${remote.path}/${file}`));
    const upToDate: string[] = [];
    for (const file of exists) {
        const expected = await remoteSize(`${remote.baseUrl}/${file}`);
        if (expected == null) {
            upToDate.push(file);
            continue;
        }
        const actual = sizes.get(`${remote.path}/${file}`);
        if (actual == expected) {
            upToDate.push(file);
            continue;
        }
        logger.debug({
            action: 'outdated',
            path: `${remote.path}/${file}`,
            expected: expected,
            actual: actual
        });
    }
    return upToDate;
}
